import type { ReactNode } from 'react';
import { Badge, Card, Field, Input } from '@paige/ui';
import type { AccountHit } from './pb-types';
import { Pager, type Paging } from './AccountTables';
import { ListState, ScrollTable, Th } from './pb-parts';
import { channelWord, stateTone, stateWord } from './pb-words';
import './partner-billing.css';

export interface AccountsBoardProps {
  q: string;
  onQ: (q: string) => void;
  accounts: AccountHit[] | undefined;
  paging: Paging;
  renderAccount: (zohoAccountId: string, name: string) => ReactNode;
}

/** The paying accounts, found on the server by name or Zoho id a page at a time: it works at thousands of partners. */
export function AccountsBoard(p: AccountsBoardProps): ReactNode {
  return (
    <Card>
      <div className="pb-accounts-search">
        <Field
          label="Find a paying account"
          hint="Part of its name, or the start of its Zoho account id"
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              type="search"
              aria-describedby={describedBy}
              value={p.q}
              onChange={(e) => p.onQ(e.target.value)}
            />
          )}
        </Field>
      </div>
      <ListState rows={p.accounts} none="No paying account matches." />
      {p.accounts?.length ? <AccountTable accounts={p.accounts} render={p.renderAccount} /> : null}
      <Pager p={p.paging} what="Accounts" />
    </Card>
  );
}

function AccountTable(p: {
  accounts: AccountHit[];
  render: AccountsBoardProps['renderAccount'];
}): ReactNode {
  const head = (
    <>
      <Th>Account</Th>
      <Th>Invoice</Th>
      <Th num>Companies</Th>
      <Th>Standing</Th>
    </>
  );
  return (
    <ScrollTable head={head} label="Paying accounts">
      {p.accounts.map((a) => (
        <tr key={a.zohoAccountId}>
          <td>
            {p.render(a.zohoAccountId, a.name)}
            <span className="pb-table__sub">
              {a.zohoAccountId} · {a.kind}
            </span>
          </td>
          <td>
            <Badge tone={a.invoiceChannel === 'quickbooks' ? 'accent' : 'neutral'}>
              {channelWord[a.invoiceChannel]}
            </Badge>
          </td>
          <td className="is-num">{a.companies.toLocaleString('en-US')}</td>
          <td>
            <Badge tone={stateTone[a.state]}>{stateWord[a.state]}</Badge>
          </td>
        </tr>
      ))}
    </ScrollTable>
  );
}
